import type {
  ListTableColumnOutput,
  ListTableSelectedRowsState,
  UseListTableColumnsProps,
  UseListTableSelectedRowsProps,
} from "./list-table.type";
import type { ListColumnOptions, ListItem } from "@/modules/list";
import type { ModelKey } from "@/modules/model";
import type { TableProps } from "antd";
import type { Key } from "react";

import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetCurrentUser } from "@/entities/current-user";
import { useAsyncActions } from "@/modules/async-actions";
import { useListContext } from "@/modules/list";

import {
  getColumnFilterInfos,
  getColumnSortInfos,
  getUserListTableColumn,
} from "./list-table.helper";

export const useListTableRowClassName = (modelKey?: ModelKey) => {
  const { isInDeletingState, isInUpdatedState, isInFailedState } =
    useAsyncActions();

  const getRowClassName = useCallback(
    (record: ListItem) => {
      if (!modelKey || !record.id) return "";
      if (isInDeletingState(modelKey, record.id))
        return "list-table-row-deleting";
      if (isInFailedState(modelKey, record.id)) return "list-table-row-failed";
      if (isInUpdatedState(modelKey, record.id))
        return "list-table-row-updated";
      return "";
    },
    [modelKey, isInDeletingState, isInUpdatedState, isInFailedState]
  );

  return { getRowClassName };
};

export const useListTableSelectedRows = <Item extends ListItem = ListItem>({
  onSelectedItemsChange,
  isBatchMode,
}: UseListTableSelectedRowsProps<Item>) => {
  const { fetching } = useListContext();

  const [selectedRows, setSelectedRows] = useState<
    ListTableSelectedRowsState<Item>
  >({ keys: [], items: [] });

  const onSelectedRowsChange = (keys: Key[], items: Item[]) => {
    setSelectedRows({ keys, items });
  };

  const resetSelectedRows = () => {
    setSelectedRows({ keys: [], items: [] });
  };

  useEffect(() => {
    onSelectedItemsChange?.(selectedRows.items);
  }, [selectedRows]);

  useEffect(() => {
    if (fetching) resetSelectedRows();
  }, [fetching]);

  const rowSelection: TableProps<Item>["rowSelection"] = {
    selectedRowKeys: selectedRows.keys,
    onChange: onSelectedRowsChange,
  };

  return {
    selectedItems: selectedRows.items,
    rowSelection: isBatchMode ? rowSelection : undefined,
  };
};

export const useListTableColumns = <Item extends ListItem = ListItem>({
  columns = [],
}: UseListTableColumnsProps<Item>) => {
  const { filters, sort, multipleSort, setColumnsOptions } = useListContext();
  const { currentUser } = useGetCurrentUser();

  const { formattedColumns, columnsOptions } = useMemo(() => {
    if (!columns?.length) return { formattedColumns: [], columnsOptions: [] };

    const formattedColumns: ListTableColumnOutput<Item>[] = [];
    const columnsOptions: ListColumnOptions[] = [];

    for (const columnInput of columns) {
      const column = getUserListTableColumn(columnInput, currentUser);

      const { filterKey, filteredValue } = getColumnFilterInfos(
        column,
        filters
      );

      const { sorter, sortKey, sortOrder } = getColumnSortInfos(
        column,
        sort,
        multipleSort
      );

      // Add formatted column based on hardcoded filter & sort props
      formattedColumns.push({
        ...column,
        title: column.titleNode ?? column.title,
        filteredValue,
        sorter,
        sortOrder,
        showSorterTooltip: false, // hide sort tooltip by default to show special tooltip (ex: column with icons)
      });

      // columnsOptions only includes visible columns (used for the column picker UI)
      if (!column.hidden) {
        columnsOptions.push({
          title: column.title,
          dataIndex: column.dataIndex,
          filterKey,
          sortKey,
        });
      }
    }

    return { formattedColumns, columnsOptions };
  }, [currentUser, columns, filters, sort, multipleSort]);

  useEffect(() => {
    setColumnsOptions(columnsOptions);
  }, [columnsOptions, setColumnsOptions]);

  return formattedColumns;
};
